import {
  ALLOWED_COST_DOCUMENT_TYPES,
  ApiError,
  COGS_PROFIT_SHARE_BPS,
  type CogsReport,
  type CogsSalesReport,
  formatMoney,
  type InventoryCost,
  type InventoryCostKind,
  MAX_COST_DOCUMENT_BYTES,
  queryKeys,
  type SalesTaxReport,
  VENDOR_PAYMENT_STATUSES,
  type VendorPaymentStatus,
} from '@mevaser/shared'
import {
  Badge,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Field,
  humanizeStatus,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Switch,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  useToast,
  VENDOR_PAYMENT_STATUS_TONE,
} from '@mevaser/ui'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import type { LucideIcon } from 'lucide-react'
import {
  Download,
  Package,
  PackageX,
  Paperclip,
  Pencil,
  Plus,
  ReceiptText,
  Trash2,
  TrendingUp,
} from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Page, PageHeader } from '../components/app-shell.js'
import { BulkActionBar, DataTable } from '../components/data-table.js'
import { fittedPartSummary, RemoveCostDialog } from '../components/fitted-parts.js'
import { firstOfMonth, MonthNav, toISODate } from '../components/month-calendar.js'
import {
  InvoiceDialog,
  type ReturnPrefill,
  ReturnStatusBadge,
  ReturnsTab,
  StartReturnDialog,
} from '../components/vendor-returns.js'
import { api, apiBaseUrl } from '../lib/api.js'
import { useAuth } from '../lib/auth.js'

const ALLOWED_COST_DOCUMENT_TYPE_LIST = ALLOWED_COST_DOCUMENT_TYPES.join(',')

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function toDateInputValue(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function StatTile({
  label,
  value,
  subLabel,
  icon: Icon,
  isLoading,
}: {
  label: string
  value: string
  subLabel?: string
  icon: LucideIcon
  isLoading?: boolean
}) {
  return (
    <Card>
      <CardContent className="flex items-start justify-between gap-3 pt-4">
        <div className="min-w-0">
          <p className="text-xs text-foreground-muted">{label}</p>
          {isLoading ? (
            <Skeleton className="mt-1.5 h-7 w-20" />
          ) : (
            <>
              <p className="mt-0.5 truncate text-2xl font-semibold tabular">{value}</p>
              {subLabel ? <p className="text-2xs text-foreground-muted">{subLabel}</p> : null}
            </>
          )}
        </div>
        <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-surface-sunken text-foreground-muted">
          <Icon className="size-4" aria-hidden="true" />
        </div>
      </CardContent>
    </Card>
  )
}

/**
 * The card machine's (Square) fee rate (t1262). The machine never tells us
 * what it charged, so the owner enters its percentage and per-sale amount once
 * and the report applies it to every card machine sale.
 */
function CardMachineFeeDialog({
  open,
  bps,
  fixedCents,
  onClose,
}: {
  open: boolean
  bps: number
  fixedCents: number
  onClose: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [percent, setPercent] = useState('')
  const [fixed, setFixed] = useState('')

  useEffect(() => {
    if (!open) return
    setPercent(String(bps / 100))
    setFixed((fixedCents / 100).toFixed(2))
  }, [open, bps, fixedCents])

  const percentN = Number(percent)
  const fixedN = Number(fixed)
  const valid =
    percent.trim() !== '' &&
    fixed.trim() !== '' &&
    Number.isFinite(percentN) &&
    percentN >= 0 &&
    percentN <= 20 &&
    Number.isFinite(fixedN) &&
    fixedN >= 0 &&
    fixedN <= 5

  const save = useMutation({
    mutationFn: () =>
      api.patch('/settings/store', {
        cardMachineFeeBps: Math.round(percentN * 100),
        cardMachineFeeFixedCents: Math.round(fixedN * 100),
      }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.purchasing.all() }),
        queryClient.invalidateQueries({ queryKey: queryKeys.storeSettings() }),
      ])
      toast.success('Card machine fee saved')
      onClose()
    },
    onError: (error: Error) =>
      toast.error('Could not save', error instanceof ApiError ? error.message : undefined),
  })

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Card machine fee</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <p className="text-sm text-foreground-muted">
            Enter what your card machine (Square) charges on each sale. It is subtracted from every
            card machine sale in the report, including earlier ones.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Percent of the sale" hint="For example 2.6">
              {(props) => (
                <Input
                  {...props}
                  inputMode="decimal"
                  value={percent}
                  onChange={(e) => setPercent(e.target.value)}
                  placeholder="0"
                />
              )}
            </Field>
            <Field label="Plus per sale" hint="In dollars, for example 0.15">
              {(props) => (
                <Input
                  {...props}
                  inputMode="decimal"
                  value={fixed}
                  onChange={(e) => setFixed(e.target.value)}
                  placeholder="0.00"
                />
              )}
            </Field>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={save.isPending}
            disabled={!valid}
            onClick={() => save.mutate()}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Revenue vs. cost of goods sold for a date range. Purely a report — no
 * mutations happen here, so it's gated on `reports:read`, not `inventory:read`
 * (sales/technician/support hold the latter but must not see margins).
 */
function CogsTab() {
  const now = new Date()
  const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1)
  const [from, setFrom] = useState(toDateInputValue(firstOfMonth))
  const [to, setTo] = useState(toDateInputValue(now))
  const [scope, setScope] = useState<'all' | 'phones'>('all')
  const [locationId, setLocationId] = useState('all')
  const [view, setView] = useState<'sales' | 'products'>('sales')
  const [feeDialogOpen, setFeeDialogOpen] = useState(false)
  const { can } = useAuth()

  const { data: locations } = useQuery({
    queryKey: queryKeys.locations(),
    queryFn: () => api.get<{ id: string; name: string; isActive: boolean }[]>('/locations'),
    enabled: can('inventory:read'),
  })

  // One filter set drives the totals, the table and the export, so what is on
  // screen is exactly what is exported.
  const filters = {
    from,
    to,
    scope,
    ...(locationId !== 'all' ? { locationId } : {}),
  }

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.purchasing.cogs(filters),
    queryFn: () => api.get<CogsReport>('/reports/cogs', { query: filters }),
    enabled: Boolean(from && to),
  })

  const { data: sales, isLoading: salesLoading } = useQuery({
    queryKey: queryKeys.purchasing.cogsSales(filters),
    queryFn: () => api.get<CogsSalesReport>('/reports/cogs/sales', { query: filters }),
    enabled: Boolean(from && to),
  })

  const margin =
    data && data.totals.revenueCents > 0
      ? `${((data.totals.grossProfitCents / data.totals.revenueCents) * 100).toFixed(1)}%`
      : '—'

  const saleColumns = useMemo<ColumnDef<CogsSalesReport['rows'][number], unknown>[]>(
    () => [
      {
        header: 'Date',
        accessorKey: 'saleDate',
        cell: ({ row }) => <span className="tabular">{row.original.saleDate}</span>,
      },
      {
        header: 'Order',
        accessorKey: 'orderNumber',
        cell: ({ row }) => (
          <Link
            to="/orders/$orderId"
            params={{ orderId: row.original.orderId }}
            className="text-primary hover:underline"
          >
            {row.original.orderNumber}
          </Link>
        ),
      },
      {
        header: 'Location',
        accessorKey: 'locationName',
        cell: ({ row }) => row.original.locationName ?? '—',
      },
      {
        header: 'Product',
        accessorKey: 'productName',
        cell: ({ row }) => (
          <div>
            {row.original.productName}
            {row.original.imei ? (
              <div className="font-identifier text-xs text-foreground-muted">
                {row.original.imei}
              </div>
            ) : null}
          </div>
        ),
      },
      {
        header: 'Qty',
        accessorKey: 'qty',
        cell: ({ row }) => <span className="tabular">{row.original.qty}</span>,
      },
      {
        header: 'Total before tax',
        accessorKey: 'revenueCents',
        cell: ({ row }) => (
          <span className="tabular">{formatMoney(row.original.revenueCents)}</span>
        ),
      },
      {
        header: 'Cost',
        accessorKey: 'cogsCents',
        cell: ({ row }) => <span className="tabular">{formatMoney(row.original.cogsCents)}</span>,
      },
      {
        header: 'Card fee',
        accessorKey: 'cardFeeCents',
        cell: ({ row }) => (
          <span className="tabular">{formatMoney(row.original.cardFeeCents)}</span>
        ),
      },
      {
        header: 'Profit',
        accessorKey: 'profitCents',
        cell: ({ row }) => (
          <span className={`tabular ${row.original.profitCents < 0 ? 'text-danger' : ''}`}>
            {formatMoney(row.original.profitCents)}
          </span>
        ),
      },
    ],
    [],
  )

  const columns = useMemo<ColumnDef<CogsReport['byProduct'][number], unknown>[]>(
    () => [
      { header: 'Product', accessorKey: 'productName' },
      {
        header: 'SKU',
        accessorKey: 'sku',
        cell: ({ row }) => <span className="font-identifier">{row.original.sku}</span>,
      },
      {
        header: 'Qty sold',
        accessorKey: 'qtySold',
        cell: ({ row }) => <span className="tabular">{row.original.qtySold}</span>,
      },
      {
        header: 'Revenue',
        accessorKey: 'revenueCents',
        cell: ({ row }) => (
          <span className="tabular">{formatMoney(row.original.revenueCents)}</span>
        ),
      },
      {
        header: 'Goods cost',
        accessorKey: 'cogsCents',
        cell: ({ row }) => <span className="tabular">{formatMoney(row.original.cogsCents)}</span>,
      },
      {
        header: 'Card fees',
        accessorKey: 'cardFeesCents',
        cell: ({ row }) => (
          <span className="tabular">{formatMoney(row.original.cardFeesCents)}</span>
        ),
      },
      {
        header: 'Gross profit',
        accessorKey: 'grossProfitCents',
        cell: ({ row }) => (
          <span className={`tabular ${row.original.grossProfitCents < 0 ? 'text-danger' : ''}`}>
            {formatMoney(row.original.grossProfitCents)}
          </span>
        ),
      },
    ],
    [],
  )

  return (
    <div className="space-y-5">
      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 pt-4">
          <Field label="From">
            {(props) => (
              <Input
                {...props}
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
              />
            )}
          </Field>
          <Field label="To">
            {(props) => (
              <Input {...props} type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            )}
          </Field>
          <Field label="Location">
            {(props) => (
              <Select value={locationId} onValueChange={setLocationId}>
                <SelectTrigger {...props} className="w-44">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All locations</SelectItem>
                  {(locations ?? []).map((l) => (
                    <SelectItem key={l.id} value={l.id}>
                      {l.name}
                      {l.isActive ? '' : ' (inactive)'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </Field>
          <Field label="Products">
            {(props) => (
              <Select value={scope} onValueChange={(v) => setScope(v as 'all' | 'phones')}>
                <SelectTrigger {...props} className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All products</SelectItem>
                  <SelectItem value="phones">Phones only</SelectItem>
                </SelectContent>
              </Select>
            )}
          </Field>
          <Button
            variant="secondary"
            disabled={!from || !to}
            onClick={() => {
              const params = new URLSearchParams({ from, to, scope })
              if (locationId !== 'all') params.set('locationId', locationId)
              window.location.assign(`${apiBaseUrl}/reports/cogs/export.csv?${params.toString()}`)
            }}
          >
            <Download /> Export
          </Button>
        </CardContent>
      </Card>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <StatTile
          label="Revenue"
          value={data ? formatMoney(data.totals.revenueCents) : '—'}
          subLabel={
            data && data.totals.rentalRevenueCents > 0
              ? `incl. ${formatMoney(data.totals.rentalRevenueCents)} rental`
              : undefined
          }
          icon={TrendingUp}
          isLoading={isLoading}
        />
        <StatTile
          label="Cost of goods sold"
          value={data ? formatMoney(data.totals.cogsCents + data.totals.cardFeesCents) : '—'}
          subLabel={
            data
              ? `${formatMoney(data.totals.cogsCents)} goods + ${formatMoney(data.totals.cardFeesCents)} card fees`
              : undefined
          }
          icon={ReceiptText}
          isLoading={isLoading}
        />
        <StatTile
          label="Gross profit"
          value={data ? formatMoney(data.totals.grossProfitCents) : '—'}
          icon={TrendingUp}
          isLoading={isLoading}
        />
        <StatTile label="Margin" value={margin} icon={TrendingUp} isLoading={isLoading} />
        <StatTile
          label={`${COGS_PROFIT_SHARE_BPS / 100}% of profit`}
          value={data ? formatMoney(data.totals.profitShareCents) : '—'}
          subLabel="of the gross profit above"
          icon={TrendingUp}
          isLoading={isLoading}
        />
      </div>

      {data ? (
        <Card>
          <CardContent className="flex flex-wrap items-center justify-between gap-3 pt-4">
            <div className="space-y-0.5 text-sm">
              <p className="font-medium">Card fees</p>
              <p className="text-foreground-muted">
                Stripe: the actual fee on each charge
                {data.totals.cardFeesPendingCount > 0
                  ? ` (${data.totals.cardFeesPendingCount} still loading from Stripe, counted as $0 for now)`
                  : ''}
              </p>
              <p className="text-foreground-muted">
                Card machine (Square):{' '}
                {data.cardMachineFee.bps === 0 && data.cardMachineFee.fixedCents === 0
                  ? data.totals.cardMachinePaymentsCount > 0
                    ? 'no rate entered yet, so its fees are not included'
                    : 'no rate entered yet'
                  : `${data.cardMachineFee.bps / 100}% + ${formatMoney(data.cardMachineFee.fixedCents)} per sale`}
              </p>
            </div>
            {can('settings:manage') ? (
              <Button variant="secondary" onClick={() => setFeeDialogOpen(true)}>
                <Pencil /> Set card machine fee
              </Button>
            ) : null}
          </CardContent>
        </Card>
      ) : null}
      {data ? (
        <CardMachineFeeDialog
          open={feeDialogOpen}
          bps={data.cardMachineFee.bps}
          fixedCents={data.cardMachineFee.fixedCents}
          onClose={() => setFeeDialogOpen(false)}
        />
      ) : null}

      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant={view === 'sales' ? 'secondary' : 'ghost'}
          onClick={() => setView('sales')}
        >
          Each sale
        </Button>
        <Button
          size="sm"
          variant={view === 'products' ? 'secondary' : 'ghost'}
          onClick={() => setView('products')}
        >
          By product
        </Button>
      </div>

      {view === 'sales' ? (
        <>
          <DataTable
            columns={saleColumns}
            data={sales?.rows ?? []}
            isLoading={salesLoading}
            empty={
              <EmptyState
                icon={Package}
                title="Nothing sold in this range"
                description="Widen the date range or location, or come back once there's a sale."
              />
            }
          />
          {sales ? (
            <p className="text-sm tabular">
              {sales.rows.length} sales{sales.locationName ? ` at ${sales.locationName}` : ''} ·
              Total before tax <strong>{formatMoney(sales.totals.revenueCents)}</strong> · Total
              profit <strong>{formatMoney(sales.totals.profitCents)}</strong> ·{' '}
              {COGS_PROFIT_SHARE_BPS / 100}% of profit{' '}
              <strong>{formatMoney(sales.totals.profitShareCents)}</strong>
            </p>
          ) : null}
        </>
      ) : (
        <DataTable
          columns={columns}
          data={data?.byProduct ?? []}
          isLoading={isLoading}
          empty={
            <EmptyState
              icon={Package}
              title="Nothing sold in this range"
              description="Widen the date range, or come back once there's a sale."
            />
          }
        />
      )}

      <p className="text-xs text-foreground-muted">
        Counted-stock cost uses each product's current cost, not what it cost when that unit was
        sold. A fully refunded order is left out entirely, since the goods came back to stock.
      </p>
      <p className="text-xs text-foreground-muted">
        Card fees are taken off every card sale, including rentals. Stripe sales use the fee Stripe
        actually charged; card machine sales use the rate above, applied to earlier sales too. A
        partly returned order keeps its full fee, because the card company does not refund it.
      </p>
      <p className="text-xs text-foreground-muted">
        Export is a CSV that opens in Excel and lists exactly the sales shown under "Each sale" for
        the dates, location and products chosen above, with totals and the{' '}
        {COGS_PROFIT_SHARE_BPS / 100}% of profit at the bottom. Prices are before sales tax. Rental
        income has no individual sales or location, so it is only counted in the tiles when every
        location and all products are shown.
      </p>
    </div>
  )
}

/**
 * Sales and sales tax owed for one calendar month, per tax category (t1166).
 * A report like `CogsTab`, so it is gated on `reports:read` too.
 */
function SalesTaxTab() {
  const [monthStart, setMonthStart] = useState(() => firstOfMonth(new Date()))
  const month = toISODate(monthStart).slice(0, 7)

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.purchasing.salesTax(month),
    queryFn: () => api.get<SalesTaxReport>('/reports/sales-tax', { query: { month } }),
  })

  const columns = useMemo<ColumnDef<SalesTaxReport['byCategory'][number], unknown>[]>(
    () => [
      {
        header: 'Tax category',
        accessorKey: 'name',
        cell: ({ row }) => (
          <span>
            {row.original.name}
            {row.original.rateBps != null ? (
              <span className="ml-1.5 text-xs text-foreground-muted">
                {(row.original.rateBps / 100).toFixed(2)}%
              </span>
            ) : null}
          </span>
        ),
      },
      {
        header: 'Total sales',
        accessorKey: 'salesCents',
        cell: ({ row }) => <span className="tabular">{formatMoney(row.original.salesCents)}</span>,
      },
      {
        header: 'Taxable sales',
        accessorKey: 'taxableSalesCents',
        cell: ({ row }) => (
          <span className="tabular">{formatMoney(row.original.taxableSalesCents)}</span>
        ),
      },
      {
        header: 'Tax-exempt sales',
        accessorKey: 'exemptSalesCents',
        cell: ({ row }) => (
          <span className="tabular">{formatMoney(row.original.exemptSalesCents)}</span>
        ),
      },
      {
        header: 'Not taxed',
        accessorKey: 'notTaxedSalesCents',
        cell: ({ row }) => (
          <span className="tabular">{formatMoney(row.original.notTaxedSalesCents)}</span>
        ),
      },
      {
        header: 'Tax owed',
        accessorKey: 'taxCents',
        cell: ({ row }) => (
          <span className="tabular font-medium">{formatMoney(row.original.taxCents)}</span>
        ),
      },
    ],
    [],
  )

  return (
    <div className="space-y-5">
      <Card>
        <CardContent className="flex flex-wrap items-center justify-between gap-3 pt-4">
          <MonthNav monthStart={monthStart} onChange={setMonthStart} />
          <Button
            variant="secondary"
            onClick={() => {
              const params = new URLSearchParams({ month })
              window.location.assign(
                `${apiBaseUrl}/reports/sales-tax/export.csv?${params.toString()}`,
              )
            }}
          >
            <Download /> Export CSV
          </Button>
        </CardContent>
      </Card>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile
          label="Total sales"
          value={data ? formatMoney(data.totals.salesCents) : '—'}
          subLabel={data ? `${data.totals.ordersCount} orders, before tax` : undefined}
          icon={TrendingUp}
          isLoading={isLoading}
        />
        <StatTile
          label="Taxable sales"
          value={data ? formatMoney(data.totals.taxableSalesCents) : '—'}
          icon={ReceiptText}
          isLoading={isLoading}
        />
        <StatTile
          label="Tax-exempt sales"
          value={data ? formatMoney(data.totals.exemptSalesCents) : '—'}
          icon={ReceiptText}
          isLoading={isLoading}
        />
        <StatTile
          label="Tax owed"
          value={data ? formatMoney(data.totals.taxCents) : '—'}
          icon={ReceiptText}
          isLoading={isLoading}
        />
      </div>

      <DataTable
        columns={columns}
        data={data?.byCategory ?? []}
        isLoading={isLoading}
        empty={
          <EmptyState
            icon={Package}
            title="Nothing sold in this month"
            description="Pick another month, or come back once there's a sale."
          />
        }
      />

      <p className="text-xs text-foreground-muted">
        Sales are line prices before tax, for orders paid in the month (store time). Items returned
        are taken out of both sales and tax, in the month of the original sale. Tax owed is what was
        charged at checkout. "Not taxed" is sales that carried no tax and weren't exempt: 0%
        categories, kiosk sales and gift cards. Shipping is never taxed
        {data && data.totals.shippingCents > 0
          ? ` (${formatMoney(data.totals.shippingCents)} this month)`
          : ''}
        , and coupons come off after tax
        {data && data.totals.discountCents > 0
          ? ` (${formatMoney(data.totals.discountCents)} this month)`
          : ''}
        . Items are filed under their product's current tax category.
      </p>
    </div>
  )
}

/** Records a purchase against a product directly — not tied to a specific
 *  serialized unit, which instead gets its purchase row from intake. */
function RecordPurchaseDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [productId, setProductId] = useState('')
  const [label, setLabel] = useState('Stock purchase')
  const [amount, setAmount] = useState('')
  const [qty, setQty] = useState('')
  const [vendor, setVendor] = useState('')
  const [invoiceRef, setInvoiceRef] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const { data: products } = useQuery({
    queryKey: queryKeys.products.list({ isActive: true }),
    queryFn: () =>
      api.get<{ items: { id: string; name: string; sku: string }[] }>('/products', {
        query: { isActive: true, pageSize: 200 },
      }),
    enabled: open,
  })

  const create = useMutation({
    mutationFn: () =>
      api.post<InventoryCost>('/costs', {
        productId,
        kind: 'purchase',
        label: label.trim(),
        amountCents: Math.round(Number(amount || 0) * 100),
        qty: qty.trim() === '' ? null : Number.parseInt(qty, 10),
        vendor: vendor.trim() || null,
        invoiceRef: invoiceRef.trim() || null,
      }),
    onSuccess: async (created) => {
      if (file) {
        try {
          const formData = new FormData()
          formData.append('file', file)
          await api.postFile(`/costs/${created.id}/document`, formData)
        } catch (error) {
          toast.error(
            'Purchase recorded, but the invoice file could not be attached',
            error instanceof ApiError ? error.message : 'Open the row and attach it from there.',
          )
        }
      }
      await queryClient.invalidateQueries({ queryKey: queryKeys.purchasing.all() })
      toast.success('Purchase recorded')
      setProductId('')
      setLabel('Stock purchase')
      setAmount('')
      setQty('')
      setVendor('')
      setInvoiceRef('')
      setFile(null)
      onClose()
    },
    onError: (error: Error) =>
      toast.error(
        'Could not record the purchase',
        error instanceof ApiError ? error.message : undefined,
      ),
  })

  function handleFileChange(picked: File | null) {
    if (!picked) {
      setFile(null)
      return
    }
    if (!(ALLOWED_COST_DOCUMENT_TYPES as readonly string[]).includes(picked.type)) {
      toast.error('Could not attach', 'Invoices must be a PDF, JPEG, PNG or WebP image.')
      return
    }
    if (picked.size > MAX_COST_DOCUMENT_BYTES) {
      toast.error('Could not attach', `${picked.name} is larger than 10 MB.`)
      return
    }
    setFile(picked)
  }

  const valid = Boolean(productId) && label.trim() !== '' && amount.trim() !== ''

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Record a purchase</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <Field label="Product" required>
            {(props) => (
              <Select value={productId} onValueChange={setProductId}>
                <SelectTrigger {...props}>
                  <SelectValue placeholder="Choose a product…" />
                </SelectTrigger>
                <SelectContent>
                  {products?.items.map((product) => (
                    <SelectItem key={product.id} value={product.id}>
                      {product.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </Field>
          <Field label="Label" required>
            {(props) => (
              <Input {...props} value={label} onChange={(e) => setLabel(e.target.value)} />
            )}
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Amount" required hint="In dollars.">
              {(props) => (
                <Input
                  {...props}
                  inputMode="decimal"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                />
              )}
            </Field>
            <Field label="Quantity" hint="Optional.">
              {(props) => (
                <Input
                  {...props}
                  inputMode="numeric"
                  value={qty}
                  onChange={(e) => setQty(e.target.value)}
                />
              )}
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Vendor">
              {(props) => (
                <Input {...props} value={vendor} onChange={(e) => setVendor(e.target.value)} />
              )}
            </Field>
            <Field label="Invoice reference">
              {(props) => (
                <Input
                  {...props}
                  value={invoiceRef}
                  onChange={(e) => setInvoiceRef(e.target.value)}
                />
              )}
            </Field>
          </div>
          <Field label="Invoice or receipt" hint="Optional. PDF, JPEG, PNG or WebP, up to 10 MB.">
            {() => (
              <div className="flex items-center gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ALLOWED_COST_DOCUMENT_TYPE_LIST}
                  className="hidden"
                  onChange={(e) => {
                    handleFileChange(e.target.files?.[0] ?? null)
                    e.target.value = ''
                  }}
                />
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Paperclip className="size-4" /> {file ? 'Replace file' : 'Choose file'}
                </Button>
                {file ? (
                  <span className="text-xs text-foreground-muted">
                    {file.name} ({formatBytes(file.size)})
                  </span>
                ) : null}
              </div>
            )}
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => {
              setFile(null)
              onClose()
            }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={create.isPending}
            disabled={!valid}
            onClick={() => create.mutate()}
          >
            Record purchase
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** Edits an existing cost row's bookkeeping fields, and its attached
 *  supplier invoice/receipt — a device's purchase row (from intake) and its
 *  add-ons land here too, since this tab lists every cost line. */
function EditPurchaseDialog({
  cost,
  onClose,
}: {
  cost: InventoryCost | null
  onClose: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [label, setLabel] = useState('')
  const [amount, setAmount] = useState('')
  const [qty, setQty] = useState('')
  const [vendor, setVendor] = useState('')
  const [invoiceRef, setInvoiceRef] = useState('')
  const [doc, setDoc] = useState<InventoryCost['document']>(null)
  const [uploading, setUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!cost) return
    setLabel(cost.label)
    setAmount((cost.amountCents / 100).toString())
    setQty(cost.qty === null ? '' : String(cost.qty))
    setVendor(cost.vendor ?? '')
    setInvoiceRef(cost.invoiceRef ?? '')
    setDoc(cost.document)
  }, [cost])

  const invalidate = () => queryClient.invalidateQueries({ queryKey: queryKeys.purchasing.all() })

  const save = useMutation({
    mutationFn: () =>
      api.patch(`/costs/${cost?.id}`, {
        label: label.trim(),
        amountCents: Math.round(Number(amount || 0) * 100),
        qty: qty.trim() === '' ? null : Number.parseInt(qty, 10),
        vendor: vendor.trim() || null,
        invoiceRef: invoiceRef.trim() || null,
      }),
    onSuccess: async () => {
      await invalidate()
      toast.success('Saved')
      onClose()
    },
    onError: (error: Error) =>
      toast.error('Could not save', error instanceof ApiError ? error.message : undefined),
  })

  async function handleFile(picked: File | null) {
    if (!picked || !cost) return
    if (!(ALLOWED_COST_DOCUMENT_TYPES as readonly string[]).includes(picked.type)) {
      toast.error('Could not attach', 'Invoices must be a PDF, JPEG, PNG or WebP image.')
      return
    }
    if (picked.size > MAX_COST_DOCUMENT_BYTES) {
      toast.error('Could not attach', `${picked.name} is larger than 10 MB.`)
      return
    }
    setUploading(true)
    try {
      const formData = new FormData()
      formData.append('file', picked)
      const updated = await api.postFile<InventoryCost>(`/costs/${cost.id}/document`, formData)
      setDoc(updated.document)
      await invalidate()
    } catch (error) {
      toast.error(
        'Could not attach the file',
        error instanceof ApiError ? error.message : undefined,
      )
    } finally {
      setUploading(false)
    }
  }

  async function removeDocument() {
    if (!cost) return
    try {
      await api.delete(`/costs/${cost.id}/document`)
      setDoc(null)
      await invalidate()
    } catch (error) {
      toast.error(
        'Could not remove the file',
        error instanceof ApiError ? error.message : undefined,
      )
    }
  }

  const valid = label.trim() !== '' && amount.trim() !== ''

  return (
    <Dialog open={cost !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit {cost?.label}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <Field label="Label" required>
            {(props) => (
              <Input {...props} value={label} onChange={(e) => setLabel(e.target.value)} />
            )}
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Amount" required hint="In dollars.">
              {(props) => (
                <Input
                  {...props}
                  inputMode="decimal"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                />
              )}
            </Field>
            <Field label="Quantity" hint="Optional.">
              {(props) => (
                <Input
                  {...props}
                  inputMode="numeric"
                  value={qty}
                  onChange={(e) => setQty(e.target.value)}
                />
              )}
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Vendor">
              {(props) => (
                <Input {...props} value={vendor} onChange={(e) => setVendor(e.target.value)} />
              )}
            </Field>
            <Field label="Invoice reference">
              {(props) => (
                <Input
                  {...props}
                  value={invoiceRef}
                  onChange={(e) => setInvoiceRef(e.target.value)}
                />
              )}
            </Field>
          </div>
          <Field label="Invoice or receipt" hint="PDF, JPEG, PNG or WebP, up to 10 MB.">
            {() => (
              <div className="flex flex-wrap items-center gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ALLOWED_COST_DOCUMENT_TYPE_LIST}
                  className="hidden"
                  onChange={(e) => {
                    void handleFile(e.target.files?.[0] ?? null)
                    e.target.value = ''
                  }}
                />
                {doc && cost ? (
                  <>
                    <a
                      href={`${apiBaseUrl}/costs/${cost.id}/document`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1.5 rounded-md border border-border px-2 py-1 text-xs text-foreground-muted hover:text-foreground"
                    >
                      <Paperclip className="size-3" />
                      {doc.filename} ({formatBytes(doc.sizeBytes)})
                    </a>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={uploading}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      Replace
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={uploading}
                      onClick={() => void removeDocument()}
                    >
                      Remove
                    </Button>
                  </>
                ) : (
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    disabled={uploading}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Paperclip className="size-4" /> {uploading ? 'Attaching…' : 'Attach invoice'}
                  </Button>
                )}
              </div>
            )}
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={save.isPending}
            disabled={!valid}
            onClick={() => save.mutate()}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** Every purchase and add-on cost line — the accounts-payable list: what's
 *  owed, to whom, and whether it's been invoiced or paid. */
function PurchasesTab({ canWrite }: { canWrite: boolean }) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [statusFilter, setStatusFilter] = useState<VendorPaymentStatus | 'all'>('all')
  const [kindFilter, setKindFilter] = useState<InventoryCostKind | 'all'>('all')
  const [q, setQ] = useState('')
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [recording, setRecording] = useState(false)
  const [editing, setEditing] = useState<InventoryCost | null>(null)
  const [removingPart, setRemovingPart] = useState<InventoryCost | null>(null)
  const [returning, setReturning] = useState<ReturnPrefill | null>(null)
  const [invoice, setInvoice] = useState<{ vendor: string; invoiceRef: string } | null>(null)

  const filters = {
    pageSize: 50,
    paymentStatus: statusFilter === 'all' ? undefined : statusFilter,
    kind: kindFilter === 'all' ? undefined : kindFilter,
    q: q || undefined,
  }

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: queryKeys.purchasing.costs(filters),
    queryFn: ({ pageParam }) =>
      api.get<{ items: InventoryCost[]; total: number; page: number; pageSize: number }>('/costs', {
        query: { ...filters, page: pageParam },
      }),
    initialPageParam: 1,
    getNextPageParam: (last) =>
      last.page * last.pageSize < last.total ? last.page + 1 : undefined,
  })
  const rows = data?.pages.flatMap((p) => p.items) ?? []
  const total = data?.pages[0]?.total ?? 0

  const invalidate = () => queryClient.invalidateQueries({ queryKey: queryKeys.purchasing.all() })

  const setStatus = useMutation({
    mutationFn: (vars: { id: string; paymentStatus: VendorPaymentStatus }) =>
      api.patch(`/costs/${vars.id}`, { paymentStatus: vars.paymentStatus }),
    onSuccess: invalidate,
    onError: (error: Error) =>
      toast.error('Could not update status', error instanceof ApiError ? error.message : undefined),
  })

  const removeCost = useMutation({
    mutationFn: (id: string) => api.delete(`/costs/${id}`),
    onSuccess: async () => {
      await invalidate()
      toast.success('Removed')
    },
    onError: (error: Error) =>
      toast.error('Could not remove', error instanceof ApiError ? error.message : undefined),
  })

  const bulkStatus = useMutation({
    mutationFn: (paymentStatus: VendorPaymentStatus) =>
      api.post('/costs/bulk-status', { ids: [...selectedIds], paymentStatus }),
    onSuccess: async () => {
      await invalidate()
      setSelectedIds(new Set())
      toast.success('Updated')
    },
    onError: (error: Error) =>
      toast.error('Could not update', error instanceof ApiError ? error.message : undefined),
  })

  const columns = useMemo<ColumnDef<InventoryCost, unknown>[]>(
    () => [
      {
        header: 'Label',
        accessorKey: 'label',
        cell: ({ row }) => (
          <div>
            {row.original.label}
            {fittedPartSummary(row.original) ? (
              <div className="text-xs text-foreground-muted">{fittedPartSummary(row.original)}</div>
            ) : null}
            {row.original.orderId && row.original.orderNumber ? (
              <div className="text-xs text-foreground-muted">
                Sold on{' '}
                <Link
                  to="/orders/$orderId"
                  params={{ orderId: row.original.orderId }}
                  className="text-primary hover:underline"
                >
                  {row.original.orderNumber}
                </Link>
              </div>
            ) : null}
            <div className="text-xs text-foreground-muted">
              {row.original.unitImei ? (
                <span className="font-identifier">{row.original.unitImei}</span>
              ) : row.original.productName ? (
                row.original.productName
              ) : (
                '—'
              )}
            </div>
          </div>
        ),
      },
      {
        header: 'Kind',
        accessorKey: 'kind',
        cell: ({ row }) => <Badge tone="neutral">{humanizeStatus(row.original.kind)}</Badge>,
      },
      {
        header: 'Amount',
        accessorKey: 'amountCents',
        cell: ({ row }) => <span className="tabular">{formatMoney(row.original.amountCents)}</span>,
      },
      { header: 'Vendor', accessorKey: 'vendor', cell: ({ row }) => row.original.vendor ?? '—' },
      {
        header: 'Invoice',
        accessorKey: 'invoiceRef',
        cell: ({ row }) => (
          <div className="inline-flex items-center gap-1.5">
            {row.original.invoiceRef && row.original.vendor ? (
              <button
                type="button"
                className="text-primary hover:underline"
                onClick={() =>
                  setInvoice({
                    vendor: row.original.vendor as string,
                    invoiceRef: row.original.invoiceRef as string,
                  })
                }
              >
                {row.original.invoiceRef}
              </button>
            ) : (
              <span>{row.original.invoiceRef ?? '—'}</span>
            )}
            {row.original.document ? (
              <a
                href={`${apiBaseUrl}/costs/${row.original.id}/document`}
                target="_blank"
                rel="noreferrer"
                aria-label={`View the attached ${row.original.document.filename}`}
                className="text-foreground-muted hover:text-foreground"
              >
                <Paperclip className="size-3.5" />
              </a>
            ) : null}
          </div>
        ),
      },
      {
        header: 'Status',
        accessorKey: 'paymentStatus',
        cell: ({ row }) => (
          <div className="space-y-1">
            {canWrite ? (
              <Select
                value={row.original.paymentStatus}
                onValueChange={(v) =>
                  setStatus.mutate({ id: row.original.id, paymentStatus: v as VendorPaymentStatus })
                }
              >
                <SelectTrigger className="h-7 w-28 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {VENDOR_PAYMENT_STATUSES.map((s) => (
                    <SelectItem key={s} value={s}>
                      {humanizeStatus(s)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Badge tone={VENDOR_PAYMENT_STATUS_TONE[row.original.paymentStatus]}>
                {humanizeStatus(row.original.paymentStatus)}
              </Badge>
            )}
            {row.original.vendorReturn ? (
              <div>
                <ReturnStatusBadge status={row.original.vendorReturn.status} />
              </div>
            ) : null}
          </div>
        ),
      },
      ...(canWrite
        ? [
            {
              header: '',
              id: 'actions',
              cell: ({ row }: { row: { original: InventoryCost } }) => (
                <div className="flex items-center gap-1">
                  {row.original.kind === 'purchase' && !row.original.vendorReturn ? (
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`${row.original.label} needs to be returned`}
                      title="Needs to be returned"
                      onClick={() =>
                        setReturning({
                          unitId: row.original.unitId,
                          imei: row.original.unitImei,
                          productId: row.original.productId,
                          productName: row.original.productName ?? row.original.label,
                          vendorName: row.original.vendor,
                          invoiceRef: row.original.invoiceRef,
                          sourceCostId: row.original.id,
                          amountCents: row.original.amountCents,
                        })
                      }
                    >
                      <PackageX />
                    </Button>
                  ) : null}
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Edit ${row.original.label}`}
                    onClick={() => setEditing(row.original)}
                  >
                    <Pencil />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Delete ${row.original.label}`}
                    onClick={() =>
                      row.original.componentProductId
                        ? setRemovingPart(row.original)
                        : removeCost.mutate(row.original.id)
                    }
                  >
                    <Trash2 />
                  </Button>
                </div>
              ),
            } satisfies ColumnDef<InventoryCost, unknown>,
          ]
        : []),
    ],
    [canWrite, setStatus, removeCost],
  )

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <Field label="Status">
          {(props) => (
            <Select
              value={statusFilter}
              onValueChange={(v) => setStatusFilter(v as typeof statusFilter)}
            >
              <SelectTrigger {...props} className="w-36">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {VENDOR_PAYMENT_STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {humanizeStatus(s)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </Field>
        <Field label="Kind">
          {(props) => (
            <Select value={kindFilter} onValueChange={(v) => setKindFilter(v as typeof kindFilter)}>
              <SelectTrigger {...props} className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All kinds</SelectItem>
                <SelectItem value="purchase">Purchase</SelectItem>
                <SelectItem value="addon">Add-on</SelectItem>
              </SelectContent>
            </Select>
          )}
        </Field>
        <Field label="Search">
          {(props) => (
            <Input
              {...props}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Label, vendor, invoice…"
            />
          )}
        </Field>
        {canWrite ? (
          <Button
            variant="secondary"
            size="sm"
            className="ms-auto"
            onClick={() => setRecording(true)}
          >
            <Plus /> Record purchase
          </Button>
        ) : null}
      </div>

      <DataTable
        columns={columns}
        data={rows}
        isLoading={isLoading}
        infinite={{
          total,
          hasMore: Boolean(hasNextPage),
          isFetchingMore: isFetchingNextPage,
          onLoadMore: () => void fetchNextPage(),
        }}
        {...(canWrite
          ? {
              getRowId: (row: InventoryCost) => row.id,
              selectedIds,
              onToggleRow: (id: string) =>
                setSelectedIds((prev) => {
                  const next = new Set(prev)
                  if (next.has(id)) next.delete(id)
                  else next.add(id)
                  return next
                }),
              onToggleAll: (ids: string[]) =>
                setSelectedIds((prev) => (prev.size === ids.length ? new Set() : new Set(ids))),
            }
          : {})}
        empty={
          <EmptyState
            icon={ReceiptText}
            title="No cost lines yet"
            description="Purchase costs recorded at intake, and any add-ons you record, show up here."
          />
        }
      />

      {canWrite ? (
        <BulkActionBar count={selectedIds.size} onClear={() => setSelectedIds(new Set())}>
          {VENDOR_PAYMENT_STATUSES.map((s) => (
            <Button
              key={s}
              variant="ghost"
              size="sm"
              loading={bulkStatus.isPending}
              onClick={() => bulkStatus.mutate(s)}
            >
              Mark {humanizeStatus(s).toLowerCase()}
            </Button>
          ))}
        </BulkActionBar>
      ) : null}

      <RecordPurchaseDialog open={recording} onClose={() => setRecording(false)} />
      <EditPurchaseDialog cost={editing} onClose={() => setEditing(null)} />
      <RemoveCostDialog cost={removingPart} onClose={() => setRemovingPart(null)} />
      <StartReturnDialog
        open={returning !== null}
        prefill={returning}
        onClose={() => setReturning(null)}
      />
      <InvoiceDialog target={invoice} onClose={() => setInvoice(null)} />
    </div>
  )
}

export function PurchasingPage() {
  const { can } = useAuth()
  const canReadCogs = can('reports:read')
  const canWriteCosts = can('inventory:write')

  return (
    <Page>
      <PageHeader
        title="Purchasing"
        description="What inventory cost, who it's owed to, and how it nets against sales."
      />

      <Tabs defaultValue={canReadCogs ? 'cogs' : 'purchases'}>
        <TabsList>
          {canReadCogs ? <TabsTrigger value="cogs">Cost of goods</TabsTrigger> : null}
          {canReadCogs ? <TabsTrigger value="sales-tax">Sales tax</TabsTrigger> : null}
          <TabsTrigger value="purchases">Purchases</TabsTrigger>
          <TabsTrigger value="returns">Returns</TabsTrigger>
        </TabsList>

        {canReadCogs ? (
          <TabsContent value="cogs">
            <CogsTab />
          </TabsContent>
        ) : null}
        {canReadCogs ? (
          <TabsContent value="sales-tax">
            <SalesTaxTab />
          </TabsContent>
        ) : null}
        <TabsContent value="purchases">
          <PurchasesTab canWrite={canWriteCosts} />
        </TabsContent>
        <TabsContent value="returns">
          <ReturnsTab canWrite={canWriteCosts} />
        </TabsContent>
      </Tabs>
    </Page>
  )
}
